import React, { useState, useCallback, useEffect } from "react";
import { Download, FileSpreadsheet, FileText, ChevronRight, Users, Filter, Layers, Loader2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import type { PipelineStage } from "../types/pipeline-stage.types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (options: ExportOptions) => void;
  selectedLeadCount: number;
  currentStatus?: string;
  isExporting?: boolean;
  pipelineStages?: PipelineStage[];
  initialScope?: 'all' | 'status' | 'selected';
}

export interface ExportOptions {
  scope: 'all' | 'status' | 'selected';
  /** Multi-select list of pipeline stages when scope === 'status'. */
  statuses?: string[];
  format: 'csv' | 'xlsx';
}

const DEFAULT_STATUSES = [
  "New lead",
  "Appt",
  "Underwriting",
  "Sold & Approved",
  "Bad number",
  "No sale",
  "Do not call",
  "Declined",
  "Lapsed",
];

type Step = 'scope' | 'status' | 'format';

export function ExportDialog({
  open,
  onOpenChange,
  onExport,
  selectedLeadCount,
  isExporting = false,
  pipelineStages,
  initialScope,
}: ExportDialogProps) {
  const [step, setStep] = useState<Step>('scope');
  const [scope, setScope] = useState<'all' | 'status' | 'selected'>('all');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // When opened with an initialScope (e.g. from "Export selected" button), jump to format step
  useEffect(() => {
    if (open && initialScope) {
      setScope(initialScope);
      if (initialScope === 'selected' || initialScope === 'all') {
        setStep('format');
      } else if (initialScope === 'status') {
        setStep('status');
      }
    }
  }, [open, initialScope]);

  // Merge default statuses with custom pipeline stages
  const allStatuses = React.useMemo(() => {
    const customNames = (pipelineStages || [])
      .filter(s => !s.isSystem && s.isVisible)
      .map(s => s.name);
    const merged = [...DEFAULT_STATUSES];
    for (const name of customNames) {
      if (!merged.includes(name)) merged.push(name);
    }
    return merged;
  }, [pipelineStages]);

  // Map a status string (system_value OR name) to its pipeline stage display name.
  // e.g. "New lead" → "Leads", "Appt" → "Appt" (already match), "CustomStage" → "CustomStage".
  // Backend still receives the original string (system_value) for filtering.
  const getStatusDisplayName = React.useCallback(
    (status: string): string => {
      const stage = (pipelineStages || []).find(
        (ps) => ps.systemValue === status || ps.name === status,
      );
      return stage?.name || status;
    },
    [pipelineStages],
  );

  const reset = useCallback(() => {
    setStep('scope');
    setScope('all');
    setSelectedStatuses([]);
  }, []);

  const toggleStatus = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  };

  const handleOpenChange = useCallback((isOpen: boolean) => {
    // DEMO SANDBOX: never open the picker — go straight to the trial prompt.
    //
    // The backend blocks the export itself, but only once a scope and format have been
    // chosen. That walked the visitor through three steps of a flow that was always
    // going to refuse, which reads as a bug rather than a boundary. The button is the
    // moment they've decided they want their data out, so make the offer there.
    if (isDemoMode() && isOpen) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Exporting leads is disabled in the demo sandbox. Start a free trial to use it for real.',
        }),
      );
      return;
    }

    if (!isOpen) reset();
    onOpenChange(isOpen);
  }, [onOpenChange, reset]);

  const handleScopeSelect = (s: 'all' | 'status' | 'selected') => {
    setScope(s);
    if (s === 'status') {
      setStep('status');
    } else {
      setStep('format');
    }
  };

  const handleStatusContinue = () => {
    if (selectedStatuses.length === 0) return;
    setStep('format');
  };

  const handleFormatSelect = (format: 'csv' | 'xlsx') => {
    onExport({
      scope,
      statuses: scope === 'status' ? selectedStatuses : undefined,
      format,
    });
    reset();
  };

  const handleBack = () => {
    if (step === 'format') {
      if (initialScope) {
        // If opened with initial scope, back goes to scope selection
        setStep('scope');
        setScope('all');
      } else {
        setStep(scope === 'status' ? 'status' : 'scope');
      }
    } else if (step === 'status') {
      setStep('scope');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="gap-2 group relative overflow-hidden border-gray-200 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200"
        >
          <Download className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5" />
          Export Leads
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="m-export-pop w-72 p-0 rounded-xl border border-gray-200 shadow-xl shadow-black/10 overflow-hidden"
      >
        {/* Header */}
        <div className="px-4 py-3 bg-primary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {step !== 'scope' && (
                <button
                  onClick={handleBack}
                  className="text-primary-foreground/60 hover:text-primary-foreground transition-colors text-xs mr-1"
                >
                  ←
                </button>
              )}
              <Download className="h-4 w-4 text-primary-foreground" />
              <span className="text-sm font-semibold text-primary-foreground">
                {step === 'scope' && 'Export Leads'}
                {step === 'status' && 'Select Status'}
                {step === 'format' && 'Choose Format'}
              </span>
            </div>
            {/* Step indicators */}
            <div className="flex gap-1">
              {['scope', 'status', 'format'].map((s, i) => {
                const stepOrder = { scope: 0, status: 1, format: 2 };
                const currentOrder = stepOrder[step as keyof typeof stepOrder];
                const thisOrder = i;
                const isActive = thisOrder <= currentOrder;
                return (
                  <div
                    key={s}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      isActive ? 'w-4 bg-primary-foreground' : 'w-2 bg-primary-foreground/30'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Step: Scope */}
        {step === 'scope' && (
          <div className="p-2">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider px-2 py-1.5">
              What to export
            </p>
            <button
              onClick={() => handleScopeSelect('all')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors group/item text-left"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Layers className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 block">All leads</span>
                <span className="text-[11px] text-gray-500">Export your entire lead database</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300 group-hover/item:text-gray-500 transition-colors" />
            </button>
            <button
              onClick={() => handleScopeSelect('status')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors group/item text-left"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <Filter className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 block">By status</span>
                <span className="text-[11px] text-gray-500">Filter by pipeline stage</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300 group-hover/item:text-gray-500 transition-colors" />
            </button>
            <button
              onClick={() => handleScopeSelect('selected')}
              disabled={selectedLeadCount === 0}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors group/item text-left disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                <Users className="h-4 w-4 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 block">Selected leads</span>
                <span className="text-[11px] text-gray-500">
                  {selectedLeadCount > 0
                    ? `${selectedLeadCount} lead${selectedLeadCount !== 1 ? 's' : ''} selected`
                    : 'Select leads from table first'}
                </span>
              </div>
              {selectedLeadCount > 0 && (
                <span className="flex-shrink-0 text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
                  {selectedLeadCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Step: Status — multi-select checkboxes */}
        {step === 'status' && (
          <>
            <div className="p-2 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between px-2 py-1.5">
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  Pipeline stages
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedStatuses(
                      selectedStatuses.length === allStatuses.length ? [] : [...allStatuses],
                    )
                  }
                  className="text-[11px] font-medium text-primary hover:underline"
                >
                  {selectedStatuses.length === allStatuses.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              {allStatuses.map((s) => {
                // Look up the pipeline stage for this status — either system (by systemValue)
                // or custom (by name) — and use its color for the status dot.
                const stage = (pipelineStages || []).find(
                  (ps) => ps.systemValue === s || ps.name === s,
                );
                const dotColor = stage?.color || "#9CA3AF"; // gray-400 fallback
                const checked = selectedStatuses.includes(s);
                return (
                  <label
                    key={s}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStatus(s)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: dotColor }}
                    />
                    <span className="text-sm text-gray-700 flex-1">{getStatusDisplayName(s)}</span>
                  </label>
                );
              })}
            </div>
            <div className="border-t border-gray-100 p-2">
              <Button
                size="sm"
                onClick={handleStatusContinue}
                disabled={selectedStatuses.length === 0}
                className="w-full"
              >
                Continue{selectedStatuses.length > 0 ? ` (${selectedStatuses.length})` : ''}
              </Button>
            </div>
          </>
        )}

        {/* Step: Format */}
        {step === 'format' && (
          <div className="p-2">
            {/* Summary pill */}
            <div className="mx-2 mb-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
              <span className="text-[11px] text-gray-500">
                {scope === 'all' && 'Exporting all leads'}
                {scope === 'status' &&
                  (selectedStatuses.length === 1
                    ? `Exporting "${getStatusDisplayName(selectedStatuses[0])}" leads`
                    : `Exporting ${selectedStatuses.length} stages: ${selectedStatuses.slice(0, 2).map(getStatusDisplayName).join(', ')}${selectedStatuses.length > 2 ? ` +${selectedStatuses.length - 2} more` : ''}`)}
                {scope === 'selected' && `Exporting ${selectedLeadCount} selected`}
              </span>
            </div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider px-2 py-1.5">
              File format
            </p>
            <button
              onClick={() => handleFormatSelect('csv')}
              disabled={isExporting}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors group/fmt text-left disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 block">CSV</span>
                <span className="text-[11px] text-gray-500">Comma-separated values</span>
              </div>
              {isExporting ? (
                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
              ) : (
                <Download className="h-4 w-4 text-gray-300 group-hover/fmt:text-amber-500 transition-colors" />
              )}
            </button>
            <button
              onClick={() => handleFormatSelect('xlsx')}
              disabled={isExporting}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors group/fmt text-left disabled:opacity-50"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 block">Excel</span>
                <span className="text-[11px] text-gray-500">Spreadsheet with formatting</span>
              </div>
              {isExporting ? (
                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
              ) : (
                <Download className="h-4 w-4 text-gray-300 group-hover/fmt:text-emerald-500 transition-colors" />
              )}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
